import {useCallback, useState} from 'react';
/**
 * Helper: Extracts products array from collection edges.
 */
const getProductsFromCollection = (collection) => {
  if (!collection) return [];
  return collection.products.map((product) => product);
};
/**
 * Helper: Gets metafield value from product safely.
 */
const getProductMetafieldValue = (product, key) => {
  return product[key]?.value;
};
/**
 * Helper: Finds filter index by ID.
 */
const findFilterIndex = (filters, filterId) => {
  return filters.findIndex((f) => f.id === filterId);
};
/**
 * Helper: Checks if a product matches all selected filters.
 * selectedFilters should only contain filters that precede the current filter.
 */
const doesProductMatchFilters = (product, selectedFilters) => {
  return Object.entries(selectedFilters).every(([key, val]) => {
    const productVal = getProductMetafieldValue(product, key);
    return productVal === val;
  });
};
/**
 * Custom hook for managing product selection with dynamic filtering.
 *
 * Features:
 * - Loads collections and filters from API
 * - Auto-selects first available product/variant on load and collection change
 * - Dynamic filter availability based on product catalog
 * - Cascade reset when changing filters (resets subsequent filters)
 * - Product selection only when all filters are selected
 *
 * @returns Object containing state and actions for product selection
 */
export function useCEProductSelector({localization} = {}) {
  // State management for collections, filters, and selections
  const [collections, setCollections] = useState([]);
  const [allFilters, setAllFilters] = useState([]);
  const [filteredFilters, setFilteredFilters] = useState([]);
  const [selectedCollection, setSelectedCollectionState] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  /**
   * Loads product selector settings from the API endpoint.
   * Fetches collections and filters, initializes filtered filters,
   * and auto-selects the first collection with its first product/variant.
   */
  const loadProductSelectorSettings = useCallback(
    async ({localization} = {}) => {
      setIsLoading(true);
      setError(null);
      try {
        const viewportWidth =
          typeof window !== 'undefined'
            ? Math.round(window.visualViewport?.width || window.innerWidth || 0)
            : 0;
        const response = await fetch(
          `${window['customizationBffUrl'] || ''}/${localization.locale}-${
            localization.market
          }/api/ce-product-selector?country=${
            localization.country
          }&viewportWidth=${viewportWidth}`,
        );
        if (!response.ok) {
          throw new Error(`Failed to fetch: ${response.statusText}`);
        }
        const data = await response.json();
        setCollections(data.collections || []);
        setAllFilters(data.filters || []);
        // Initialize filtered filters
        const initialFilteredFilters = (data.filters || []).map((filter) => ({
          ...filter,
          disabled: false,
          availableValues: filter.values.map((valueObj) => ({
            ...valueObj,
            disabled: false,
          })),
        }));
        setFilteredFilters(initialFilteredFilters);
        // Auto-select first collection and first available product/variant
        if (data.collections && data.collections.length > 0) {
          const firstCollection = data.collections[0];
          setSelectedCollectionState(firstCollection);
          //selectFirstAvailableProductAndVariant(firstCollection, data.filters);
          const updatedFilters = updateFilters(
            firstCollection,
            data.filters,
            {},
          );
          setFilterValue(
            updatedFilters[0].id,
            updatedFilters[0].availableValues.find((v) => !v.disabled)?.id ||
              '',
            firstCollection,
            data.filters,
          );
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to load product selector';
        setError(errorMessage);
        console.error('Failed to load product selector settings:', err);
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  /**
   * Finds a product that matches all selected filter values.
   * Compares product metafields against filter selections.
   *
   * @param collection - The collection to search in
   * @param filters - Object with filter IDs as keys and selected values
   * @returns Matching product or null if not found
   */
  const findMatchingProduct = useCallback((collection, filters) => {
    const products = getProductsFromCollection(collection);
    return (
      products.find((product) => {
        return Object.entries(filters).every(([key, value]) => {
          const productMetaValue = getProductMetafieldValue(product, key);
          return productMetaValue === value;
        });
      }) || null
    );
  }, []);
  /**
   * Updates filtered filters based on available products and current selections.
   * - First filter: always enabled (allows starting the selection)
   * - Subsequent filters: disabled if no products match the combination
   * Ensures users can only select valid filter combinations.
   *
   * @param collection - The current collection
   * @param allFiltersData - All available filters
   * @param selectedFiltersData - Currently selected filter values
   * @returns Array of filters with availability status
   */
  const updateFilters = useCallback(
    (collection, allFiltersData, selectedFiltersData) => {
      const products = getProductsFromCollection(collection);
      if (products.length === 0) {
        return allFiltersData.map((filter) => ({
          ...filter,
          disabled: false,
          availableValues: filter.values.map((valueObj) => ({
            ...valueObj,
            disabled: false,
          })),
        }));
      }
      return allFiltersData.map((filter, filterIndex) => {
        // First filter should not depend on any selection
        const isFirstFilter = filterIndex === 0;
        const availableValues = filter.values.map((valueObj) => {
          if (isFirstFilter) {
            const isAvailable = products.some((product) => {
              const productMetaValue = getProductMetafieldValue(
                product,
                filter.id,
              );
              if (productMetaValue !== valueObj.id) return false;
              return true;
            });
            return {
              ...valueObj,
              disabled: !isAvailable,
            };
          }
          // Check if this value is available in products
          // considering already selected filters (only filters preceding this one)
          const precedingFilterIds = new Set(
            allFiltersData.slice(0, filterIndex).map((f) => f.id),
          );
          const precedingSelectedFilters = Object.fromEntries(
            Object.entries(selectedFiltersData).filter(([key]) =>
              precedingFilterIds.has(key),
            ),
          );
          const isAvailable = products.some((product) => {
            const productMetaValue = getProductMetafieldValue(
              product,
              filter.id,
            );
            if (productMetaValue !== valueObj.id) return false;
            // Check only preceding selected filters
            return doesProductMatchFilters(product, precedingSelectedFilters);
          });
          return {
            ...valueObj,
            disabled: !isAvailable,
          };
        });
        return {
          ...filter,
          disabled: false,
          availableValues,
        };
      });
    },
    [],
  );
  /**
   * Sets a filter value and updates the product selection state.
   * - Keeps existing filter values if they're still compatible with the new selection
   * - Auto-fills missing filters with first available values to ensure a product is always selected
   * - Updates variant selection, keeping current if compatible or selecting first available
   *
   * @param filterId - The ID of the filter to update
   * @param value - The new value for the filter
   */
  const setFilterValue = useCallback(
    (filterId, value, collection, allFiltersDefault) => {
      collection = collection || selectedCollection;
      allFiltersDefault = allFiltersDefault || allFilters;
      const products = getProductsFromCollection(collection);
      if (products.length === 0) return;
      // Start with current filter change
      const newSelectedFilters = {
        ...selectedFilters,
        [filterId]: value,
      };
      // Auto-fill missing filters and validate existing ones
      allFiltersDefault.forEach((filter) => {
        if (filter.id === filterId) return; // Already set above
        // Build selectedFilters containing only filters preceding this one
        const filterIdx = findFilterIndex(allFiltersDefault, filter.id);
        const precedingFilterIds = new Set(
          allFiltersDefault.slice(0, filterIdx).map((f) => f.id),
        );
        const precedingSelectedFilters = Object.fromEntries(
          Object.entries(newSelectedFilters).filter(([key]) =>
            precedingFilterIds.has(key),
          ),
        );
        const currentValue = newSelectedFilters[filter.id];
        // If filter has no value, set first available
        if (!currentValue) {
          const firstAvailableValue = filter.values.find((valueObj) => {
            return products.some((product) => {
              const productMetaValue = getProductMetafieldValue(
                product,
                filter.id,
              );
              if (productMetaValue !== valueObj.id) return false;
              // Check if this product matches only preceding selected filters
              return doesProductMatchFilters(product, precedingSelectedFilters);
            });
          });
          if (firstAvailableValue) {
            newSelectedFilters[filter.id] = firstAvailableValue.id;
          }
          return;
        }
        // If filter has a value, check if it's still available with preceding filter selections
        const isCurrentValueStillAvailable = products.some((product) => {
          // Check if this product has the current value for this filter
          const productMetaValue = getProductMetafieldValue(product, filter.id);
          if (productMetaValue !== currentValue) return false;
          // Check if this product matches only preceding selected filters
          return doesProductMatchFilters(product, precedingSelectedFilters);
        });
        // If current value is no longer available, find first available
        if (!isCurrentValueStillAvailable) {
          const firstAvailableValue = filter.values.find((valueObj) => {
            return products.some((product) => {
              const productMetaValue = getProductMetafieldValue(
                product,
                filter.id,
              );
              if (productMetaValue !== valueObj.id) return false;
              // Check if this product matches only preceding selected filters
              return doesProductMatchFilters(product, precedingSelectedFilters);
            });
          });
          if (firstAvailableValue) {
            newSelectedFilters[filter.id] = firstAvailableValue.id;
          }
        }
        // If current value is still available, keep it (do nothing)
      });
      setSelectedFilters(newSelectedFilters);
      // Find matching product (should always find one with auto-filled filters)
      const matchingProduct = findMatchingProduct(
        collection,
        newSelectedFilters,
      );
      setSelectedProduct(matchingProduct);
      // Handle variant selection
      if (matchingProduct) {
        let newVariant = null;
        // First, try to find a variant that matches the previously selected size
        if (selectedSize) {
          const sameSize = matchingProduct.variants.find((variant) => {
            return variant.title === selectedSize && variant.availableForSale;
          });
          if (sameSize) {
            newVariant = sameSize;
          }
        }
        // If no valid variant found, select first available
        if (!newVariant) {
          const firstAvailableVariant =
            matchingProduct.variants.find(
              (variant) => variant.availableForSale,
            ) || matchingProduct.variants[0];
          newVariant = firstAvailableVariant || null;
        }
        setSelectedVariant(newVariant);
        setSelectedSize(newVariant?.title || null);
      } else {
        setSelectedVariant(null);
        setSelectedSize(null);
      }
      // Update filtered filters
      const newFilteredFilters = updateFilters(
        collection,
        allFiltersDefault,
        newSelectedFilters,
      );
      setFilteredFilters(newFilteredFilters);
    },
    [
      selectedFilters,
      selectedCollection,
      selectedSize,
      allFilters,
      findMatchingProduct,
      updateFilters,
    ],
  );
  /**
   * Sets the selected collection and auto-selects first product/variant.
   * Updates filtered filters based on the new collection's available products.
   *
   * @param collection - The collection to select
   */
  const setCollection = useCallback(
    (collection, filters) => {
      setSelectedCollectionState(collection);
      filters = filters || allFilters;
      // Calculate filtered filters first
      const newFilteredFilters = updateFilters(collection, filters, {});
      setFilteredFilters(newFilteredFilters);
      // Set first available value from first filter
      if (
        newFilteredFilters.length > 0 &&
        newFilteredFilters[0].availableValues.length > 0
      ) {
        setFilterValue(
          newFilteredFilters[0].id,
          newFilteredFilters[0].availableValues.find((v) => !v.disabled)?.id ||
            '',
          collection,
        );
      }
    },
    [allFilters, updateFilters, setFilterValue],
  );
  /**
   * Sets the selected variant (size/option).
   *
   * @param variant - The product variant to select
   */
  const setVariant = useCallback((variant) => {
    setSelectedVariant(variant);
    setSelectedSize(variant.title);
  }, []);
  /**
   * Resets all selections to initial state.
   * Clears collection, product, variant, and filter selections.
   */
  const reset = useCallback(() => {
    setSelectedCollectionState(null);
    setSelectedProduct(null);
    setSelectedVariant(null);
    setSelectedSize(null);
    setSelectedFilters({});
  }, []);
  return {
    collections,
    allFilters,
    filteredFilters,
    selectedCollection,
    selectedProduct,
    selectedVariant,
    selectedSize,
    selectedFilters,
    isLoading,
    error,
    initProductSelector: loadProductSelectorSettings,
    setCollection,
    setFilterValue,
    setVariant,
    setSelectedSize,
    reset,
  };
}
